import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  INCIDENT_STATUSES,
  type AppRegistryEntry,
  type Incident,
  type IncidentStatus,
  type IncidentType,
} from "@feedback-hub/contract/browser";

import { ALL_APPS, ApiError, batchUpdateStatus, listApps, listIncidents } from "../api.js";
import {
  ADMIN_STATUS_LABELS,
  TYPE_LABELS,
  bodyPreview,
  categoryLabel,
  formatAge,
} from "../format.js";
import { StatusBadge } from "./StatusBadge.js";

const TYPES: IncidentType[] = ["bug", "feature", "contact", "rating", "nps"];

const selectionKey = (i: Pick<Incident, "appId" | "incidentId">): string =>
  `${i.appId} ${i.incidentId}`;

function splitSelectionKey(key: string): { appId: string; incidentId: string } {
  const [appId, incidentId] = key.split(" ");
  return { appId: appId ?? "", incidentId: incidentId ?? "" };
}

export interface QueueProps {
  onOpen: (incident: Incident) => void;
}

export function Queue({ onOpen }: QueueProps) {
  const [apps, setApps] = useState<AppRegistryEntry[]>([]);
  const [appId, setAppId] = useState<string>(ALL_APPS);
  const [status, setStatus] = useState<IncidentStatus | "">("new");
  const [type, setType] = useState<IncidentType | "">("");
  const [surfaceId, setSurfaceId] = useState("");
  const [items, setItems] = useState<Incident[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<Array<string | undefined>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkStatus, setBulkStatus] = useState<IncidentStatus | "">("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsg, setBulkMsg] = useState<{ tone: "ok" | "warn" | "error"; text: string } | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await listApps();
        if (cancelled) return;
        setApps(res.apps);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Failed to load apps.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(
    async (pageCursor?: string, resetStack = false) => {
      if (!appId) return;
      setLoading(true);
      setError(null);
      setSelected(new Set());
      try {
        const page = await listIncidents({
          appId,
          status,
          type,
          surfaceId: surfaceId.trim() || undefined,
          limit: 50,
          cursor: pageCursor,
        });
        setItems(page.items);
        setCursor(pageCursor);
        setNextCursor(page.cursor);
        if (resetStack) setCursorStack([]);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Failed to load incidents.");
        setItems([]);
        setNextCursor(undefined);
      } finally {
        setLoading(false);
      }
    },
    [appId, status, type, surfaceId],
  );

  useEffect(() => {
    void load(undefined, true);
  }, [load]);

  function nextPage() {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, cursor]);
    void load(nextCursor);
  }

  function prevPage() {
    const stack = [...cursorStack];
    const prev = stack.pop();
    setCursorStack(stack);
    void load(prev);
  }

  const allOnPageSelected = items.length > 0 && items.every((i) => selected.has(selectionKey(i)));
  const someOnPageSelected = items.some((i) => selected.has(selectionKey(i)));
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someOnPageSelected && !allOnPageSelected;
    }
  }, [someOnPageSelected, allOnPageSelected]);

  function toggleOne(incident: Incident) {
    const key = selectionKey(incident);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleAllOnPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const i of items) next.delete(selectionKey(i));
      } else {
        for (const i of items) next.add(selectionKey(i));
      }
      return next;
    });
  }

  const selectedCount = selected.size;
  const bulkNoteRequired = bulkStatus === "wont_fix";

  async function applyBulk() {
    if (!bulkStatus || selectedCount === 0 || bulkBusy) return;
    if (bulkNoteRequired && !bulkNote.trim()) {
      setBulkMsg({ tone: "error", text: "A note is required to set issues to Won't fix." });
      return;
    }
    setBulkBusy(true);
    setBulkMsg(null);
    try {
      const targets = [...selected].map(splitSelectionKey);
      const res = await batchUpdateStatus(targets, bulkStatus, bulkNote.trim() || undefined);
      const failed = res.results.filter((r) => !r.ok);
      const appliedLabel = `${res.applied} issue${res.applied === 1 ? "" : "s"}`;
      if (failed.length === 0) {
        setBulkMsg({
          tone: "ok",
          text: `Updated ${appliedLabel} to ${ADMIN_STATUS_LABELS[res.status]}.`,
        });
      } else {
        const reasons = [...new Set(failed.map((f) => f.error ?? "unknown error"))].join("; ");
        setBulkMsg({
          tone: "warn",
          text: `Updated ${appliedLabel}; ${failed.length} skipped (${reasons}).`,
        });
      }
      setBulkStatus("");
      setBulkNote("");
      await load(cursor);
    } catch (err) {
      setBulkMsg({
        tone: "error",
        text: err instanceof ApiError ? err.message : "Bulk update failed.",
      });
    } finally {
      setBulkBusy(false);
    }
  }

  const combined = appId === ALL_APPS;
  const heading = useMemo(() => {
    if (combined) return "All applications";
    return apps.find((a) => a.appId === appId)?.displayName ?? appId;
  }, [combined, apps, appId]);

  return (
    <div>
      <div className="filters panel">
        <div className="field">
          <label htmlFor="filter-app">App</label>
          <select id="filter-app" value={appId} onChange={(e) => setAppId(e.target.value)}>
            <option value={ALL_APPS}>All applications</option>
            {apps.map((a) => (
              <option key={a.appId} value={a.appId}>
                {a.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="filter-status">Status</label>
          <select
            id="filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as IncidentStatus | "")}
          >
            <option value="">All</option>
            {INCIDENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ADMIN_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="filter-type">Type</label>
          <select
            id="filter-type"
            value={type}
            onChange={(e) => setType(e.target.value as IncidentType | "")}
          >
            <option value="">All</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="filter-surface">Surface</label>
          <input
            id="filter-surface"
            value={surfaceId}
            onChange={(e) => setSurfaceId(e.target.value)}
            placeholder="optional"
          />
        </div>
        <button type="button" className="btn secondary" onClick={() => void load(undefined, true)}>
          Refresh
        </button>
      </div>

      <p className="muted" style={{ margin: "0 0 0.5rem" }}>
        {heading}
        {combined && " · feedback from every app you can see"}
      </p>

      {selectedCount > 0 && (
        <div className="panel bulk-bar">
          <span className="bulk-count">
            {selectedCount} selected
          </span>
          <select
            aria-label="Bulk status"
            value={bulkStatus}
            disabled={bulkBusy}
            onChange={(e) => setBulkStatus(e.target.value as IncidentStatus | "")}
          >
            <option value="">Set status to…</option>
            {INCIDENT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ADMIN_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <input
            aria-label="Bulk note"
            className="bulk-note"
            value={bulkNote}
            disabled={bulkBusy}
            onChange={(e) => setBulkNote(e.target.value)}
            placeholder={bulkNoteRequired ? "Note (required for Won't fix)" : "Note (optional)"}
          />
          <button
            type="button"
            className="btn"
            disabled={bulkBusy || !bulkStatus}
            onClick={() => void applyBulk()}
          >
            {bulkBusy ? "Applying…" : `Apply to ${selectedCount}`}
          </button>
          <button
            type="button"
            className="btn ghost"
            disabled={bulkBusy}
            onClick={() => setSelected(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      {bulkMsg && (
        <p
          className={
            bulkMsg.tone === "ok" ? "muted" : bulkMsg.tone === "warn" ? "warn-text" : "error"
          }
        >
          {bulkMsg.text}
        </p>
      )}

      {loading && <p className="muted">Loading queue…</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <p className="muted">No incidents match these filters.</p>
      )}

      {items.length > 0 && (
        <div className="panel table-wrap">
          <table className="queue">
            <thead>
              <tr>
                <th className="col-check">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                  />
                </th>
                <th>Ref</th>
                <th>App</th>
                <th>Type</th>
                <th>Category</th>
                <th>Status</th>
                <th>Occ</th>
                <th>Age</th>
                <th>Preview</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const key = selectionKey(item);
                const isSelected = selected.has(key);
                return (
                  <tr
                    key={key}
                    className={isSelected ? "row-selected" : undefined}
                    onClick={() => onOpen(item)}
                  >
                    <td
                      className="col-check"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleOne(item);
                      }}
                    >
                      <input
                        type="checkbox"
                        aria-label={`Select ${item.ref}`}
                        checked={isSelected}
                        onChange={() => toggleOne(item)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="mono">
                      {item.ref}
                      {item.emailStatus === "failed" && (
                        <span className="warn-mark" title="Email delivery failed">
                          email failed
                        </span>
                      )}
                    </td>
                    <td>{item.appId}</td>
                    <td>
                      {TYPE_LABELS[item.type]}
                      {item.rating ? ` / ${item.rating}` : ""}
                    </td>
                    <td>{categoryLabel(item.category)}</td>
                    <td>
                      <StatusBadge status={item.status} />
                    </td>
                    <td>{item.occurrences}</td>
                    <td>{formatAge(item.createdAt)}</td>
                    <td className="preview">{bodyPreview(item.body)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="pager">
            <button
              type="button"
              className="btn secondary"
              disabled={cursorStack.length === 0}
              onClick={prevPage}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn secondary"
              disabled={!nextCursor}
              onClick={nextPage}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
